/**
 * Sentry Error Tracking Integration
 * Centralizes error reporting with user context and performance monitoring
 */

import * as Sentry from '@sentry/react';

const isDevelopment = import.meta.env.MODE === 'development';

/**
 * Initialize Sentry with custom configuration
 * Only enabled in production or when SENTRY_DSN is provided
 */
export const initSentry = () => {
  // Get DSN from environment or use placeholder for development
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  
  // Skip initialization if no DSN is provided
  if (!dsn) {
    console.log('[Sentry] No DSN provided, skipping initialization');
    return;
  }

  Sentry.init({
    dsn,
    environment: isDevelopment ? 'development' : 'production',
    
    // Performance Monitoring
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    
    // Performance monitoring sample rates
    tracesSampleRate: isDevelopment ? 1.0 : 0.1, // 100% in dev, 10% in prod
    
    // Session Replay
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
    
    // Filter out noise
    beforeSend(event, hint) {
      // Filter out development errors
      if (isDevelopment && !event.exception) {
        return null;
      }
      
      // Filter out known non-critical errors
      const error = hint.originalException;
      if (error && typeof error === 'object' && 'message' in error) {
        const message = String(error.message);
        
        // Skip ResizeObserver errors (browser quirk)
        if (message.includes('ResizeObserver')) {
          return null;
        }
        
        // Skip network errors that are user-initiated
        if (message.includes('Failed to fetch') || message.includes('NetworkError')) {
          return null;
        }
      }
      
      return event;
    },
    
    // Enrich events with custom tags
    beforeBreadcrumb(breadcrumb) {
      // Don't log console breadcrumbs in production (too noisy)
      if (!isDevelopment && breadcrumb.category === 'console') {
        return null;
      }
      return breadcrumb;
    },
  });
};

/**
 * Set user context for Sentry
 */
export const setSentryUser = (user: { id: string; email?: string } | null) => {
  if (user) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
    });
  } else {
    Sentry.setUser(null);
  }
};

/**
 * Capture custom exception with context
 */
export const captureException = (
  error: Error,
  context?: {
    tags?: Record<string, string>;
    extra?: Record<string, any>;
    level?: Sentry.SeverityLevel;
  }
) => {
  Sentry.captureException(error, {
    level: context?.level || 'error',
    tags: context?.tags,
    extra: context?.extra,
  });
};

/**
 * Capture custom message
 */
export const captureMessage = (
  message: string,
  level: Sentry.SeverityLevel = 'info'
) => {
  Sentry.captureMessage(message, level);
};

/**
 * Start a performance span
 */
export const startSpan = (name: string) => {
  return Sentry.startSpan({ name }, (span) => span);
};

/**
 * Add breadcrumb for debugging
 */
export const addBreadcrumb = (
  message: string,
  category: string,
  data?: Record<string, any>
) => {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: 'info',
  });
};

export { Sentry };
